/**
 * Durable "payment surface saw global auth" signal.
 * Survives remount/reload via sessionStorage so leave-on-auth-loss still fires
 * after React state / refs are reset (G2).
 *
 * Key prefix: `customer:payment-auth-seen:{tenantCode}:{surfaceKey}`
 */

const KEY_PREFIX = 'customer:payment-auth-seen:';

export function paymentSurfaceAuthSeenStorageKey(
  tenantCode: string,
  surfaceKey: string,
): string {
  return `${KEY_PREFIX}${tenantCode}:${surfaceKey}`;
}

function tenantKeyPrefix(tenantCode: string): string {
  return `${KEY_PREFIX}${tenantCode}:`;
}

function readStorage(): Storage | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return null;
  }
  return window.sessionStorage;
}

export function markPaymentSurfaceAuthSeen(
  tenantCode: string,
  surfaceKey: string,
): void {
  const storage = readStorage();
  if (storage === null || tenantCode.length === 0 || surfaceKey.length === 0) {
    return;
  }
  try {
    storage.setItem(paymentSurfaceAuthSeenStorageKey(tenantCode, surfaceKey), '1');
  } catch {
    // sessionStorage may be unavailable in private mode
  }
}

export function readPaymentSurfaceAuthSeen(
  tenantCode: string,
  surfaceKey: string,
): boolean {
  const storage = readStorage();
  if (storage === null || tenantCode.length === 0 || surfaceKey.length === 0) {
    return false;
  }
  try {
    return storage.getItem(paymentSurfaceAuthSeenStorageKey(tenantCode, surfaceKey)) === '1';
  } catch {
    return false;
  }
}

export function clearPaymentSurfaceAuthSeen(
  tenantCode: string,
  surfaceKey: string,
): void {
  const storage = readStorage();
  if (storage === null || tenantCode.length === 0 || surfaceKey.length === 0) {
    return;
  }
  try {
    storage.removeItem(paymentSurfaceAuthSeenStorageKey(tenantCode, surfaceKey));
  } catch {
    // sessionStorage may be unavailable in private mode
  }
}

/**
 * Clears every payment-auth-seen key for a tenant (all surface keys).
 * Called from `endCustomerSession` alongside journey-checkpoint clear (G3).
 */
export function clearAllPaymentSurfaceAuthSeenForTenant(tenantCode: string): void {
  const storage = readStorage();
  if (storage === null || tenantCode.length === 0) {
    return;
  }
  const prefix = tenantKeyPrefix(tenantCode);
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null && key.startsWith(prefix)) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      storage.removeItem(key);
    }
  } catch {
    // sessionStorage may be unavailable in private mode
  }
}

/** @deprecated Prefer clearAllPaymentSurfaceAuthSeenForTenant — same behavior. */
export const clearPaymentSurfaceAuthSeenForTenant = clearAllPaymentSurfaceAuthSeenForTenant;

/** Test helper — wipe all payment-auth-seen keys. */
export function resetPaymentSurfaceAuthSeenForTests(): void {
  const storage = readStorage();
  if (storage === null) {
    return;
  }
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null && key.startsWith(KEY_PREFIX)) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      storage.removeItem(key);
    }
  } catch {
    // ignore
  }
}
